import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import type { AuthenticationStatus } from '@wecasa/auth/src/authentication-context';

import { ReactComponent as LogoW } from '@wecasa/common/src/assets/images/logo-w.svg';
import { ReactComponent as LogoWecasa } from '@wecasa/common/src/assets/images/logo-wecasa.svg';
import type { UniverseReference, User } from '@wecasa/common/src/types/commons';
import colors from '@wecasa/common/src/utils/colors';

import { translate } from '@wecasa/i18n';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Button from '@wecasa/web-ui/src/components/button/button';
import Icon from '@wecasa/web-ui/src/components/icon/icon';
import { StylelessButton } from '@wecasa/web-ui/src/components/utils';
import { DESKTOP_GRID_WIDTH } from '@wecasa/web-ui/src/constants/layout';
import { DesktopAndTablet, Mobile } from '@wecasa/web-ui/src/styles/media-queries';
import { minWidth } from '@wecasa/web-ui/src/styles/mixins';

import { getBackTitle, route, SIGN_IN_PATH } from '../../steps/steps';
import { trackSignInEvent } from '../../utils/tracking';
import { isLoginOrSignup } from './utils';

export const HEADER_HEIGHT = 58;

const PATHS_WITHOUT_CONNECT = [route(SIGN_IN_PATH)];

const Wrapper = styled.div`
  height: ${HEADER_HEIGHT}px;
  background-color: ${colors.white100};
  display: flex;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 11;
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 0 20px;

  ${minWidth.xl`
    width: ${DESKTOP_GRID_WIDTH}px;
  `};
`;

const StyledIcon = styled(Icon)`
  transition: all 0.3s ease-in-out;
  margin-right: 10px;
`;

const BackButton = styled(StylelessButton)`
  display: flex;
  align-items: center;
`;

const Connect = styled.div`
  max-width: 40%;
  text-align: right;
`;

const logoStyle = css`
  position: absolute;
  inset: 0;
  margin: auto;
  height: 18px;
`;

const StyledLogoW = styled(LogoW)`
  ${logoStyle}
`;

const StyledLogoWecasa = styled(LogoWecasa)`
  ${logoStyle}
`;

const t = translate('screens.main_container');

export type HeaderProps = {
  authenticationStatus: AuthenticationStatus;
  user?: User;
  openLoginScreen: () => void;
  universe: UniverseReference;
};

const Header = ({ authenticationStatus, user, openLoginScreen, universe }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const onBack = useCallback(() => {
    if (isLoginOrSignup(location.pathname)) {
      trackSignInEvent({
        eventType: 'BackButtonClick',
        universe,
        path: location.pathname,
      });
    }
    navigate(-1);
  }, [location.pathname, universe, navigate]);
  const { siteUrl, locale } = window.wecasa;
  const title = getBackTitle(universe, location);

  return (
    <Wrapper>
      <Container>
        {title && (
          <BackButton onClick={onBack} title={title}>
            <StyledIcon name='back' size={12} color={colors.ink100} />
            <Text.ButtonSM>{title}</Text.ButtonSM>
          </BackButton>
        )}
        <a href={`${siteUrl}${locale}`}>
          <Mobile>
            <StyledLogoW />
          </Mobile>
          <DesktopAndTablet>
            <StyledLogoWecasa />
          </DesktopAndTablet>
        </a>
        <Connect>
          {authenticationStatus === 'authenticated' && user?.first_name && (
            <Text>{t('.hello', { firstName: user.first_name })}</Text>
          )}
          {authenticationStatus === 'unauthenticated' && !PATHS_WITHOUT_CONNECT.includes(location.pathname) && (
            <Button onPress={openLoginScreen} variant='secondary' size='sm'>
              {t('.connect')}
            </Button>
          )}
        </Connect>
      </Container>
    </Wrapper>
  );
};

export default Header;
